/**
 * Cart drawer upsell ("Complete Your Kit").
 *
 * Loaded once from header-actions.liquid. All behaviour is delegated from
 * `document` so it keeps working after the cart drawer re-renders its markup
 * via the Section Rendering API (which replaces innerHTML and would drop any
 * directly-bound listeners).
 *
 * - "Add" buttons add the product to the cart with /cart/add.js and dispatch
 *   the theme's `cart:update` event so the drawer (and this upsell) re-render.
 * - Prev/next arrows scroll the horizontal track; their enabled/hidden state
 *   is kept in sync with the track via a ResizeObserver + scroll listener.
 */
(() => {
  if (window.__cartUpsellInit) return;
  window.__cartUpsellInit = true;

  const GAP = 12;

  /** Collect cart section ids so /cart/add.js returns rendered HTML for them. */
  const collectSectionIds = () => {
    const ids = new Set();
    document
      .querySelectorAll('cart-drawer-component, cart-items-component[data-section-id]')
      .forEach((el) => {
        const id = el.dataset.sectionId || el.id?.replace(/^.*-section-/, '');
        if (id) ids.add(id);
      });
    document.querySelectorAll('[data-section-id][id*="cart"]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  };

  /* ---- Add to cart (delegated) ------------------------------------------ */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-upsell-add]');
    if (!form) return;

    e.preventDefault();
    const btn = form.querySelector('button');
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }

    try {
      const formData = new FormData(form);
      const sectionIds = collectSectionIds();
      if (sectionIds) formData.append('sections', sectionIds);

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const addJson = await addRes.json();
      if (!addRes.ok || addJson.status) {
        throw new Error(addJson.message || 'Add to cart failed');
      }

      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cart-upsell',
            data: {
              source: 'cart-upsell',
              variantId: formData.get('id'),
              itemCount: cart.item_count,
              sections: addJson.sections || null,
            },
          },
        })
      );
    } catch (err) {
      console.error('Cart upsell add failed', err);
      form.submit(); // fall back to a full-page add
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  });

  /* ---- Horizontal scroller nav ------------------------------------------ */
  const trackOf = (root) => root?.querySelector('[data-upsell-track]');

  const updateNav = (root) => {
    if (!root) return;
    const track = trackOf(root);
    const nav = root.querySelector('[data-upsell-nav]');
    if (!track || !nav) return;

    const overflowing = track.scrollWidth - track.clientWidth > 2;
    nav.hidden = !overflowing;
    if (!overflowing) return;

    const prev = nav.querySelector('[data-upsell-prev]');
    const next = nav.querySelector('[data-upsell-next]');
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  };

  document.addEventListener('click', (e) => {
    const prev = e.target.closest('[data-upsell-prev]');
    const next = e.target.closest('[data-upsell-next]');
    if (!prev && !next) return;
    const root = (prev || next).closest('[data-cart-upsell]');
    const track = trackOf(root);
    if (!track) return;
    const card = track.querySelector('.cart-upsell__card');
    const step = (card ? card.getBoundingClientRect().width : 150) + GAP;
    track.scrollBy({ left: prev ? -step : step, behavior: 'smooth' });
  });

  // Keep arrow state in sync while scrolling (capture: scroll doesn't bubble).
  document.addEventListener(
    'scroll',
    (e) => {
      const track = e.target?.closest?.('[data-upsell-track]');
      if (track) updateNav(track.closest('[data-cart-upsell]'));
    },
    true
  );

  // A ResizeObserver covers first paint, drawer open (0 → real size), viewport
  // resize, and image loads — recomputing overflow/arrow state each time.
  const observed = new WeakSet();
  const ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      updateNav(entry.target.closest('[data-cart-upsell]'));
    }
  });

  const setup = () => {
    document.querySelectorAll('[data-upsell-track]').forEach((track) => {
      if (!observed.has(track)) {
        ro.observe(track);
        observed.add(track);
      }
      updateNav(track.closest('[data-cart-upsell]'));
    });
  };

  // Re-run after the drawer re-renders (new track nodes replace the old ones).
  document.addEventListener('cart:update', () => setTimeout(setup, 60));
  window.addEventListener('resize', () => setTimeout(setup, 60));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
